import {
  FC,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  StyledPopupList,
  StyledPopupListAnchor,
  StyledPopupListButton,
} from './PopupList.styles';

export interface PopupListProps extends PropsWithChildren {
  /** the list of items to show in the popup */
  items: ((close: () => void) => ReactNode) | ReactNode;
  /** a description of the popup for screen readers and other AT */
  popupDescription: string;
  visible?: boolean;
  setVisible?: (visible: boolean) => void;
}

/**
 * A popup list-based modal. Useful for showing a list of actions when clicking an element.
 * Wrap the component you want to bind the click to like so:
 *
 * ```jsx
 * <PopupList
 *   items={<>
 *     ...
 *   </>}
 *   >
 *   <ElementToAnchorPopupTo />
 * </PopupList>
 * ```
 *
 * Important: Make sure none of the children are clickable/have click event listeners
 */
export const PopupList: FC<PopupListProps> = ({
  children,
  items,
  popupDescription,
}) => {
  const [visible, setVisible] = useState(false);
  const previouslyFocusedRef = useRef<Element | null>(null);
  const defaultFocusRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const toggleVisible: MouseEventHandler = (e) => {
    e.stopPropagation();
    setVisible(!visible);
  };
  const closePopup = () => {
    setVisible(false);
  };
  useEffect(() => {
    if (visible) {
      document.body.addEventListener('click', closePopup);
      // enable focus within the drawer (React/TS don't like the inert attr yet)
      popupRef.current?.removeAttribute('inert');
      // Store a reference to the previously focused element and set the focus
      // to the first element with `autofocus` within the children or the close
      // button.
      previouslyFocusedRef.current = document.activeElement;
      const focusTarget =
        popupRef.current?.querySelector(
          'button,a,[href],input,select,textarea,[autofocus],[tabindex]:not([tabindex="-1"])',
        ) || defaultFocusRef.current;
      (focusTarget as HTMLFormElement)?.focus();
    } else {
      document.body.removeEventListener('click', closePopup);
      // prevent focus within the drawer
      popupRef.current?.setAttribute('inert', '');
      // re-focus previously-focused element
      (previouslyFocusedRef.current as HTMLFormElement)?.focus();
      previouslyFocusedRef.current = null;
    }
    return () => {
      document.body.removeEventListener('click', closePopup);
    };
  }, [visible]);
  return (
    <StyledPopupListAnchor>
      <StyledPopupListButton
        onClick={toggleVisible}
        ref={defaultFocusRef}
        title={`${visible ? 'Hide' : 'Show'} ${popupDescription}`}
        aria-label={`${visible ? 'Hide' : 'Show'} ${popupDescription}`}
      >
        {children}
      </StyledPopupListButton>
      <StyledPopupList
        data-testid="popup-list"
        ref={popupRef}
        $visible={visible}
        onClick={((e) => e.stopPropagation()) as MouseEventHandler}
      >
        {typeof items === 'function' ? items(() => closePopup()) : items}
      </StyledPopupList>
    </StyledPopupListAnchor>
  );
};

export default PopupList;
